import { makeAutoObservable, runInAction } from "mobx";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  createSwayyProduct,
  getSwayyProductDetail,
  isCancelledSwayyRequest,
  listSwayyBrands,
  listSwayyCategories,
  normalizeSwayyApiError,
  SwayyApiError,
  SwayyCategoryApi,
  SwayyProductDetailApi,
  updateSwayyProduct,
  updateSwayyProductCategories,
} from "../api/SwayyProductsAPI";
import {
  SwayyProductDetail,
  SwayyProductDetailLoadState,
} from "../models/products/SwayyProductDetail";
import {
  SwayyProductCreatePayload,
  SwayyProductInfoFormValues,
  SwayyProductUpdatePayload,
} from "../models/products/SwayyProductForm";
import {
  asAttributeRecord,
  buildProductAttributesPayload,
  emptyProductAttributeFields,
  haveAttributeFieldsChanged,
  parseProductAttributeFields,
} from "../models/products/SwayyProductAttributes";
import { validateProductForm, productCreateFormSchema } from "../validation/swayyFormSchemas";

export type SwayyProductFieldKey = keyof SwayyProductInfoFormValues;

export type SwayyProductFieldErrors = Partial<
  Record<SwayyProductFieldKey, string>
>;

const createDefaultForm = (): SwayyProductInfoFormValues => ({
  name: "",
  slug: "",
  description: "",
  brandId: null,
  primaryCategoryId: null,
  additionalCategoryIds: [],
  hsnCode: "",
  taxRate: 0,
  isB2c: true,
  isB2b: false,
  ...emptyProductAttributeFields(),
});

const areSortedIdListsEqual = (a: number[], b: number[]) =>
  JSON.stringify([...a].sort((x, y) => x - y)) ===
  JSON.stringify([...b].sort((x, y) => x - y));

const buildCategoryIds = (
  primaryCategoryId: number,
  additionalCategoryIds: number[],
): number[] => [
  primaryCategoryId,
  ...additionalCategoryIds.filter((id) => id !== primaryCategoryId),
];

/** Map API Zod paths → form fields. */
const API_PATH_TO_FIELD: Record<string, SwayyProductFieldKey> = {
  name: "name",
  primary_category_id: "primaryCategoryId",
  category_id: "primaryCategoryId",
  hsn_code: "hsnCode",
  tax_rate: "taxRate",
  priority: "priority",
  length: "length",
  breadth: "breadth",
  height: "height",
  weight: "weight",
};

const mapApiErrorToFieldErrors = (
  error: SwayyApiError,
): SwayyProductFieldErrors => {
  const fieldErrors: SwayyProductFieldErrors = {};

  error.details.forEach((detail) => {
    const leaf =
      detail.path
        .replace(/\[\d+\]/g, "")
        .split(".")
        .filter(Boolean)
        .pop() || detail.path;
    const field = API_PATH_TO_FIELD[leaf];
    if (field && !fieldErrors[field]) {
      fieldErrors[field] = detail.message;
    }
  });

  return fieldErrors;
};

export class SwayyProductDetailStore {
  product: SwayyProductDetail | null = null;
  form: SwayyProductInfoFormValues = createDefaultForm();
  private originalForm: SwayyProductInfoFormValues = createDefaultForm();
  brandOptions: FilterValue[] = [];
  categoryOptions: FilterValue[] = [];
  loadState: SwayyProductDetailLoadState = "idle";
  isLoadingOptions = false;
  isSaving = false;
  isCreateMode = false;
  errorMessage = "";
  fieldErrors: SwayyProductFieldErrors = {};
  private detailAbortController?: AbortController;
  private optionsAbortController?: AbortController;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get hasChanges(): boolean {
    return JSON.stringify(this.form) !== JSON.stringify(this.originalForm);
  }

  /** Primary or additional categories differ from last saved detail. */
  get hasCategoryChanges(): boolean {
    return (
      this.form.primaryCategoryId !== this.originalForm.primaryCategoryId ||
      !areSortedIdListsEqual(
        this.form.additionalCategoryIds,
        this.originalForm.additionalCategoryIds,
      )
    );
  }

  get canCreate(): boolean {
    return productCreateFormSchema.safeParse(this.form).success;
  }

  get hasFieldErrors(): boolean {
    return Object.keys(this.fieldErrors).length > 0;
  }

  prepareForCreate(): void {
    this.isCreateMode = true;
    this.product = null;
    this.form = createDefaultForm();
    this.originalForm = createDefaultForm();
    this.loadState = "ready";
    this.errorMessage = "";
    this.fieldErrors = {};
  }

  markNotFound(message = "This product could not be found."): void {
    this.loadState = "notFound";
    this.errorMessage = message;
    this.product = null;
  }

  async loadProduct(productId: number): Promise<SwayyApiError | null> {
    this.isCreateMode = false;
    this.detailAbortController?.abort();
    const controller = new AbortController();
    this.detailAbortController = controller;
    this.loadState = "loading";
    this.errorMessage = "";
    this.fieldErrors = {};

    try {
      const data = await getSwayyProductDetail(productId, controller.signal);
      runInAction(() => {
        this.product = this.mapProduct(data.product);
        this.form = this.mapProductToForm(this.product, data.categories);
        this.originalForm = { ...this.form };
        this.loadState = "ready";
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error)) return null;
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to load product details. Please try again.",
      );
      runInAction(() => {
        if (apiError.status === 404) {
          this.loadState = "notFound";
          this.errorMessage = "This product could not be found.";
        } else if (apiError.status === 403) {
          this.loadState = "forbidden";
          this.errorMessage =
            "You don’t have permission to view this product.";
        } else {
          this.loadState = "error";
          this.errorMessage = apiError.message;
        }
        this.product = null;
      });
      return apiError;
    }
  }

  async loadFilterOptions(): Promise<SwayyApiError | null> {
    this.optionsAbortController?.abort();
    const controller = new AbortController();
    this.optionsAbortController = controller;
    this.isLoadingOptions = true;

    try {
      const [brands, categories] = await Promise.all([
        listSwayyBrands(controller.signal),
        listSwayyCategories(controller.signal),
      ]);
      runInAction(() => {
        this.brandOptions = brands.map((brand) => ({
          id: String(brand.id),
          value: brand.name,
        }));
        this.categoryOptions = this.flattenCategories(categories);
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error)) return null;
      return normalizeSwayyApiError(
        error,
        "Unable to load brand and category options.",
      );
    } finally {
      runInAction(() => {
        this.isLoadingOptions = false;
      });
    }
  }

  updateForm<K extends keyof SwayyProductInfoFormValues>(
    key: K,
    value: SwayyProductInfoFormValues[K],
  ): void {
    this.form = { ...this.form, [key]: value };
    if (this.fieldErrors[key]) {
      const next = { ...this.fieldErrors };
      delete next[key];
      this.fieldErrors = next;
    }
  }

  discardChanges(): void {
    this.form = { ...this.originalForm };
    this.fieldErrors = {};
  }

  private setFieldErrors(errors: SwayyProductFieldErrors): void {
    this.fieldErrors = errors;
  }

  private applyApiFieldErrors(error: SwayyApiError): boolean {
    const fieldErrors = mapApiErrorToFieldErrors(error);
    if (Object.keys(fieldErrors).length === 0) return false;
    this.setFieldErrors(fieldErrors);
    return true;
  }

  async createProduct(): Promise<
    { productId: number } | { error: SwayyApiError }
  > {
    const clientErrors = validateProductForm(this.form, {
      requireRequiredFields: true,
    });
    if (Object.keys(clientErrors).length > 0) {
      this.setFieldErrors(clientErrors);
      return { error: new SwayyApiError("Validation failed", 422) };
    }

    this.fieldErrors = {};
    const payload = this.mapFormToCreatePayload(this.form);
    this.isSaving = true;
    try {
      const data = await createSwayyProduct(payload);
      const product = this.mapProduct(data.product);
      runInAction(() => {
        this.product = product;
        this.form = this.mapProductToForm(product, data.categories);
        this.originalForm = { ...this.form };
        this.isCreateMode = false;
        this.fieldErrors = {};
      });
      return { productId: product.id };
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to create product. Please try again.",
      );
      runInAction(() => {
        this.applyApiFieldErrors(apiError);
      });
      return { error: apiError };
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  async saveProduct(): Promise<SwayyApiError | null> {
    if (!this.product || !this.hasChanges) {
      return new SwayyApiError("No changes to save.");
    }

    const primaryChanged =
      this.form.primaryCategoryId !== this.originalForm.primaryCategoryId;

    const payload = this.buildUpdatePayload(this.form, this.originalForm);
    const categoriesChanged =
      !areSortedIdListsEqual(
        this.form.additionalCategoryIds,
        this.originalForm.additionalCategoryIds,
      ) || primaryChanged;

    if (Object.keys(payload).length === 0 && !categoriesChanged) {
      return new SwayyApiError("No changes to save.");
    }

    const clientErrors = validateProductForm(this.form, {
      requireRequiredFields: categoriesChanged,
    });
    if (Object.keys(clientErrors).length > 0) {
      this.setFieldErrors(clientErrors);
      return new SwayyApiError("Validation failed", 422);
    }

    this.fieldErrors = {};
    this.isSaving = true;
    try {
      let latestProduct = this.product;

      if (Object.keys(payload).length > 0) {
        const data = await updateSwayyProduct(this.product.id, payload);
        latestProduct = this.mapProduct(data.product);
      }

      if (categoriesChanged && this.form.primaryCategoryId !== null) {
        await updateSwayyProductCategories(
          this.product.id,
          buildCategoryIds(
            this.form.primaryCategoryId,
            this.form.additionalCategoryIds,
          ),
        );
      }

      runInAction(() => {
        this.product = latestProduct;
        this.originalForm = { ...this.form };
        this.fieldErrors = {};
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to save product changes. Please try again.",
      );
      runInAction(() => {
        this.applyApiFieldErrors(apiError);
      });
      return apiError;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  dispose(): void {
    this.detailAbortController?.abort();
    this.optionsAbortController?.abort();
  }

  private mapFormToCreatePayload(
    form: SwayyProductInfoFormValues,
  ): SwayyProductCreatePayload {
    const primaryCategoryId = form.primaryCategoryId as number;
    const trimmedSlug = form.slug.trim();
    const trimmedHsn = form.hsnCode.trim();
    const attributes = buildProductAttributesPayload(form, null);

    return {
      name: form.name.trim(),
      primary_category_id: primaryCategoryId,
      category_ids: buildCategoryIds(
        primaryCategoryId,
        form.additionalCategoryIds,
      ),
      ...(trimmedSlug && { slug: trimmedSlug }),
      brand_id: form.brandId,
      description: form.description.trim() ? form.description : null,
      is_b2c: form.isB2c,
      is_b2b: form.isB2b,
      hsn_code: trimmedHsn || null,
      tax_rate: form.taxRate ?? 0,
      ...(attributes ? { attributes } : {}),
    };
  }

  private buildUpdatePayload(
    form: SwayyProductInfoFormValues,
    original: SwayyProductInfoFormValues,
  ): SwayyProductUpdatePayload {
    const payload: SwayyProductUpdatePayload = {};

    if (form.name.trim() !== original.name.trim()) {
      payload.name = form.name.trim();
    }
    if (form.slug.trim() !== original.slug.trim()) {
      payload.slug = form.slug.trim();
    }
    if (form.description !== original.description) {
      payload.description = form.description;
    }
    if (form.brandId !== original.brandId) {
      payload.brand_id = form.brandId;
    }
    if (form.primaryCategoryId !== original.primaryCategoryId) {
      payload.primary_category_id = form.primaryCategoryId;
    }
    if (form.hsnCode.trim() !== original.hsnCode.trim()) {
      payload.hsn_code = form.hsnCode.trim() || null;
    }
    if (form.taxRate !== original.taxRate) {
      payload.tax_rate = form.taxRate;
    }
    if (form.isB2c !== original.isB2c) {
      payload.is_b2c = form.isB2c;
    }
    if (form.isB2b !== original.isB2b) {
      payload.is_b2b = form.isB2b;
    }
    if (haveAttributeFieldsChanged(form, original)) {
      payload.attributes = buildProductAttributesPayload(
        form,
        this.product?.attributes ?? null,
      );
    }

    return payload;
  }

  private mapProductToForm(
    product: SwayyProductDetail,
    categories: Array<{ id: number }> = [],
  ): SwayyProductInfoFormValues {
    const primaryCategoryId = product.primaryCategoryId;
    return {
      name: product.name,
      slug: product.slug,
      description: product.description,
      brandId: product.brandId,
      primaryCategoryId,
      additionalCategoryIds: categories
        .map((category) => category.id)
        .filter((id) => id !== primaryCategoryId),
      hsnCode: product.hsnCode,
      taxRate: product.taxRate,
      isB2c: product.isB2c,
      isB2b: product.isB2b,
      ...parseProductAttributeFields(product.attributes),
    };
  }

  private mapProduct(product: SwayyProductDetailApi): SwayyProductDetail {
    const attributes = asAttributeRecord(product.attributes);
    return {
      id: product.id,
      uuid: product.uuid,
      sku: product.sku || "—",
      name: product.name,
      slug: product.slug || "",
      brandId: product.brand_id,
      primaryCategoryId: product.primary_category_id,
      description: product.description || "",
      status: product.status,
      isB2c: Boolean(product.is_b2c),
      isB2b: Boolean(product.is_b2b),
      hsnCode: product.hsn_code || "",
      taxRate: product.tax_rate,
      attributes: Object.keys(attributes).length > 0 ? attributes : null,
      createdAdmin: product.created_admin,
      updatedAdmin: product.updated_admin,
      createdAt: product.created_at,
      updatedAt: product.updated_at,
    };
  }

  private flattenCategories(
    categories: SwayyCategoryApi[],
    ancestors: string[] = [],
  ): FilterValue[] {
    return categories.flatMap((category) => {
      const labels = [...ancestors, category.name];
      return [
        { id: String(category.id), value: labels.join(" / ") },
        ...this.flattenCategories(category.children || [], labels),
      ];
    });
  }
}
